import React, { useState, useEffect } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/User/Header/Header';
import Hero from './components/User/Hero/Hero';
import About from './components/User/About/About';
import Gallery from './components/User/Gallery/Gallery';
import ProductCard from './components/User/ProductCard/ProductCard';
import ProductModal from './components/User/ProductModal/ProductModal';
import Testimonials from './components/User/Testimonials/Testimonials';
import Contacts from './components/User/Contacts/Contacts';
import Footer from './components/User/Footer/Footer';
import SuccessPage from './components/SuccessPage/SuccessPage';
import SeoHead from './seo/SeoHead';
import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from './seo/siteConfig';
import publicApi from './services/publicApi';
import { initTelegramWebApp } from './services/telegramWebApp';

const LegacyAdminRedirect = () => <Navigate to="/" replace />;

function MainSite() {
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadProducts();
        const refresh = () => {
            if (document.visibilityState === 'visible') loadProducts(false);
        };
        document.addEventListener('visibilitychange', refresh);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', refresh);
            window.removeEventListener('focus', refresh);
        };
    }, []);

    const loadProducts = async (showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
                setError(null);
            }
            const data = await publicApi.catalog.getAll();
            setProducts(data);
            setSelectedProduct(current => current
                ? data.find(product => product.id === current.id) || null
                : null);
            setError(null);
        } catch (err) {
            if (showLoading) setError('Ошибка загрузки товаров');
            console.error(err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
    };

    const handleCloseModal = () => {
        setSelectedProduct(null);
    };

    const catalogProducts = [...products].sort((left, right) => {
        const leftPriority = left.category === 'tshirt' ? 0 : 1;
        const rightPriority = right.category === 'tshirt' ? 0 : 1;
        return leftPriority - rightPriority || left.id - right.id;
    });

    return (
        <div className="App">
            <SeoHead title={DEFAULT_TITLE} description={DEFAULT_DESCRIPTION} path="/" />
            <Header />
            <main>
                <Hero
                    products={products}
                    loading={loading}
                    error={error}
                    onRetry={loadProducts}
                    onProductClick={handleProductClick}
                />

                <section id="products" className="products-section" aria-labelledby="products-title">
                    <div className="container">
                        <div className="products-header">
                            <div>
                                <p className="section-kicker">Коллекция</p>
                                <h2 id="products-title">Выберите свою.</h2>
                            </div>
                            <p className="products-intro">
                                Футболки и лонгсливы из актуального наличия. Нажмите на вещь,
                                чтобы посмотреть ракурсы, выбрать размер и увидеть реальный остаток.
                            </p>
                        </div>

                        {loading && <div className="loading">Загрузка товаров...</div>}
                        {error && <div className="error" role="alert">{error}</div>}

                        {!loading && !error && (
                            <div className="products-grid">
                                {catalogProducts.map(product => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        onProductClick={handleProductClick}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <Gallery products={products} />
                <About />

                {selectedProduct && (
                    <ProductModal
                        product={selectedProduct}
                        onClose={handleCloseModal}
                    />
                )}

                <Testimonials />
                <Contacts />
            </main>
            <Footer />
        </div>
    );
}

function App() {
    useEffect(() => {
        initTelegramWebApp();
    }, []);

    return (
        <Routes>
            <Route
                path="/admin/*"
                element={<LegacyAdminRedirect />}
            />
            <Route
                path="/success"
                element={
                    <>
                        <SeoHead title="Статус оплаты" path="/success" noIndex />
                        <SuccessPage />
                    </>
                }
            />
            <Route path="/*" element={<MainSite />} />
        </Routes>
    );
}

export default App;
